import { useNavigate } from 'react-router-dom';
import { Heart, Globe, Shield, Clock } from 'lucide-react';

export default function DeliveryPolicy() {
  const navigate = useNavigate();

  return (
    <div className="app-container">
      <div className="px-4 py-6 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/')} className="text-white">
            ← Back
          </button>
          <Heart className="text-primary fill-primary" size={24} />
          <div className="w-8" />
        </div>

        <h1 className="text-white font-bold text-3xl mb-6">Shipping & Delivery Policy</h1>

        <div className="space-y-6 text-gray-300 text-sm leading-relaxed">
          {/* Notice */}
          <div className="glass rounded-xl p-4 border border-primary/30">
            <p className="text-white">
              BlindMatch is a <strong>100% digital service</strong>. There is no physical shipping involved. This page explains how our digital service is delivered to you.
            </p>
          </div>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
              <Clock className="text-amber-400" size={20} />
              Digital Service Delivery
            </h2>
            <p>
              BlindMatch is a matchmaking service that connects you with compatible users based on your preferences, interests, and location.
            </p>
            <div className="glass rounded-xl p-4 border border-amber-500/30 mt-3">
              <p className="text-amber-400">
                <strong>Important:</strong> Match availability depends on several factors including your location, interests, age preferences, and the availability of compatible users in our system. We cannot guarantee instant matches.
              </p>
            </div>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Matches appear in your Dashboard when compatible users are found</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Chat functionality is available once you have matches</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>No download or installation required - works in your browser</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
              <Globe className="text-blue-400" size={20} />
              Service Availability
            </h2>
            <div className="glass rounded-xl p-4 border border-white/10">
              <p className="mb-3">
                <strong>Currently Available:</strong>
              </p>
              <ul className="space-y-1 mb-4">
                <li>🇮🇳 India - Full service (UPI, Cards, Net Banking)</li>
              </ul>
              <p className="mb-3">
                <strong>Coming Soon:</strong>
              </p>
              <ul className="space-y-1 text-gray-400">
                <li>🇺🇸 United States</li>
                <li>🇬🇧 United Kingdom</li>
                <li>🇨🇦 Canada</li>
                <li>🇦🇺 Australia</li>
                <li>Other countries - Stay tuned!</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">What You Receive</h2>
            <div className="space-y-3">
              <div className="glass rounded-xl p-4 border border-white/10">
                <h3 className="text-white font-medium mb-2">3 Matches Package (₹249)</h3>
                <ul className="space-y-1 text-gray-400">
                  <li>• Up to 3 anonymous matches based on your preferences</li>
                  <li>• Unlimited chat with your matches</li>
                  <li>• End-to-end encrypted conversations</li>
                  <li>• Matches remain active until you unmatch</li>
                </ul>
              </div>
              
              <div className="glass rounded-xl p-4 border border-white/10">
                <h3 className="text-white font-medium mb-2">10 Matches Package (₹499)</h3>
                <ul className="space-y-1 text-gray-400">
                  <li>• Up to 10 anonymous matches based on your preferences</li>
                  <li>• Unlimited chat with your matches</li>
                  <li>• End-to-end encrypted conversations</li>
                  <li>• Matches remain active until you unmatch</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
              <Shield className="text-green-400" size={20} />
              Our Commitment
            </h2>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>If no matches are found immediately, you're added to our waitlist</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>We notify you via email when matches become available</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Your purchased match credits never expire</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>If technical issues on our end prevent service delivery, you're eligible for a refund</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">No Physical Shipping</h2>
            <p>
              Since BlindMatch is entirely digital, there is:
            </p>
            <ul className="mt-2 space-y-1">
              <li>• No physical product shipped</li>
              <li>• No shipping charges</li>
              <li>• No delivery address required</li>
              <li>• No courier or logistics involved</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">Exchange Policy</h2>
            <div className="glass rounded-xl p-4 border border-amber-500/30">
              <p className="text-amber-400">
                As this is a digital matchmaking service, exchanges are not applicable. Each match is unique and generated based on real-time availability of compatible users.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">Support</h2>
            <p>
              If you experience any issues with service delivery, contact us:
            </p>
            <ul className="mt-2 space-y-1">
              <li><strong>Email:</strong> <a href="mailto:support@blindmatch.in" className="text-primary hover:underline">support@blindmatch.in</a></li>
              <li><strong>Support:</strong> 24/7</li>
            </ul>
          </section>

          <section className="text-xs text-gray-500">
            <p>Last updated: March 2026</p>
          </section>
        </div>
      </div>
    </div>
  );
}
